import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import { ArrowRight, BadgeCheck, FileText, Shield, DollarSign, ShieldCheck } from "lucide-react";
import { motion } from "framer-motion";
import {
  FaShieldAlt, // For Zero Depreciation
  FaTools, // For Engine Protection
  FaTruckPickup, // For Roadside Assistance
  FaCogs, // For Consumables Cover
  FaFileInvoiceDollar, // For Return to Invoice
  FaGavel, // For Mandatory by Law
  FaRoad, // For Conclusion
} from "react-icons/fa";
import FAQSchema from "../../FAQSchema";
import AdBanner from "../AdPage/AdBanner";

function Icon({ id, open }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={2}
      stroke="currentColor"
      className={`${id === open ? "rotate-180" : ""} h-5 w-5 transition-transform`}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M19.5 8.25l-7.5 7.5-7.5-7.5"
      />
    </svg>
  );
}

const HondaCB750HornetInsurance = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [insuranceData, setInsuranceData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const faqs = [
    {
      question: "Is insurance mandatory for the CB750 Hornet?",
      answer: "Yes. Third-party liability coverage is required by law.",
    },
    {
      question: "How much does it typically cost?",
      answer: "Third-party: ₹2,500–₹4,000; Comprehensive: ₹7,500–₹12,000; With add-ons: ₹10,000–₹15,000.",
    },
    {
      question: "Which policy offers the best protection?",
      answer: "Comprehensive with zero depreciation, engine protection, and RTI is ideal.",
    },
    {
      question: "Does insurance cover engine damage?",
      answer: "Yes, with the Engine Protection add-on.",
    },
    {
      question: "Can I transfer NCB from another bike?",
      answer: "Yes, No-Claim Bonus is transferable.",
    },
    {
      question: "Is online renewal possible?",
      answer: "Yes. You can renew via insurer portals or platforms like TheFinMaster.",
    },
    {
      question: "What if my policy lapses?",
      answer: "You lose NCB and may require inspection for renewal.",
    },
  ];

  const toggleAccordion = (index) => {
    setActiveIndex(activeIndex === index ? null : index);
  };

  const cardsNew = [
    {
      title: "Zero Depreciation Cover",
      description: "Full settlement without depreciation deductions.",
      icon: <FaShieldAlt className="text-4xl text-red-500 mb-4" />,
    },
    {
      title: "Engine Protection Cover",
      description: "Safeguards against engine damage due to waterlogging or oil leakage.",
      icon: <FaTools className="text-4xl text-green-500 mb-4" />,
    },
    {
      title: "Roadside Assistance",
      description: "Offers towing, fuel assistance, battery jump-start, and on-the-spot minor repairs.",
      icon: <FaTruckPickup className="text-4xl text-blue-500 mb-4" />,
    },
    {
      title: "Consumables Cover",
      description: "Covers costs of nuts, bolts, lubricants, and essential small parts.",
      icon: <FaCogs className="text-4xl text-teal-600 mb-4" />,
    },
    {
      title: "Return to Invoice (RTI) Cover",
      description: "Pays full invoice value if the bike is stolen or totaled.",
      icon: <FaFileInvoiceDollar className="text-4xl text-orange-600 mb-4" />,
    },
  ];

  const features = [
    { icon: "🛡️", text: "Reliable & Accurate Estimates" },
    { icon: "⏱️", text: "Takes Less Than a Minute" },
    { icon: "📉", text: "Instantly Shows Premium Breakup" },
    { icon: "📄", text: "No Paperwork or Documentation" },
    { icon: "📲", text: "Easy to Use on Any Device" },
    { icon: "💰", text: "Helps You Save on Insurance" },
  ];

  const cardVariants = {
    hidden: { opacity: 0, y: 40 },
    visible: (i) => ({
      opacity: 1,
      y: 0,
      transition: { delay: i * 0.2, duration: 0.6, ease: "easeOut" },
    }),
  };

  const tableData = [
    {
      type: "Third-Party Only",
      premium: "₹2,500 – ₹4,000",
      color: "orange",
    },
    {
      type: "Comprehensive",
      premium: "₹7,500 – ₹12,000",
      color: "teal",
    },
    {
      type: "Comprehensive + Add-ons",
      premium: "₹10,000 – ₹15,000",
      color: "indigo",
    },
  ];

  const steps = [
    "Report the incident to your insurer promptly.",
    "Mandatory in cases of theft or major accidents.",
    "Include policy, RC, DL, FIR (if applicable), and repair bills.",
    "A surveyor assesses the damage.",
    "Choose cashless repair at a network garage or opt for reimbursement.",
  ];

  return (
    <>
      <Helmet>
        <title>Honda CB750 Hornet Bike Insurance in India – Compare Quotes & Buy Online</title>
        <meta
          name="description"
          content="Explore Honda CB750 Hornet bike insurance options in India. Compare quotes, customize coverage with zero depreciation, engine protection, and roadside assistance add-ons, and protect your premium streetfighter motorcycle."
        />
        <meta
          name="keywords"
          content="honda cb750 hornet bike insurance, streetfighter bike insurance, two-wheeler insurance india"
        />
        <link rel="canonical" href="https://thefinmaster.com/honda-cb750-hornet-bike-insurance" />
        <meta name="robots" content="max-snippet:-1, max-image-preview:standard" />
        <meta name="robots" content="max-video-preview:-1" />
      </Helmet>
      <FAQSchema faqs={faqs} />
      <div className="w-full flex justify-center items-center px-4 mt-20">
        <div className="w-full max-w-5xl h-auto object-contain rounded-lg shadow-md transition duration-300 hover:scale-105">
          <AdBanner displayOrder="1" />
        </div>
      </div>
      <nav className="text-gray-500 text-sm my-4 ml-10" aria-label="Breadcrumb">
        <ol className="list-none p-0 inline-flex">
          <li className="flex items-center">
            <Link to="/" className="text-blue-600 hover:text-blue-800">Home</Link>
            <svg
              className="w-4 h-4 mx-2 text-gray-500"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M7.293 14.707a1 1 0 010-1.414L11.586 9 7.293 4.707a1 1 0 111.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0z"
                clipRule="evenodd"
              />
            </svg>
          </li>
          <li className="flex items-center">
            <span className="text-gray-500">Honda CB750 Hornet Bike Insurance</span>
          </li>
        </ol>
      </nav>
      <div className="w-full flex flex-col items-center">
        <div className="w-full flex justify-center">
          <div className="w-full sm:w-[95%] flex flex-col sm:flex-row justify-center items-center">
            <div className="w-full sm:w-[50%]">
              <div className="w-[90%]">
                <h1 className="text-3xl md:text-5xl font-bold mt-6 md:mt-0 md:pl-4 pl-6">
                  <span className="text-blue-600">Honda CB750 Hornet</span> Bike Insurance Guide
                </h1>
                <p className="text-lg md:text-base lg:text-lg mt-4 pl-4">
                  The Honda CB750 Hornet is a premium streetfighter motorcycle recently introduced in India under Honda’s BigWing Topline and BigWing dealership networks. It stands out with its aggressive “Aggressive X Pure” design—sharp lines, muscular tank shrouds, and LED lighting. Priced at ₹8.59 lakh (ex-showroom), it’s positioned as a mid-capacity naked bike option for performance enthusiasts in India.
                </p>
              </div>
            </div>
            <div className="w-full sm:w-[50%]">
              <img
                className="w-full max-w-full mt-2"
                src="/assets/cb750hornet-insurance.jpeg"
                alt="Honda CB750 Hornet"
              />
            </div>
          </div>
        </div>
      </div>
      <div className="w-full flex justify-center items-center my-6 px-4">
        <div className="w-full max-w-[984px]">
          <AdBanner displayOrder="2" />
        </div>
      </div>
      <div className="bg-gradient-to-br from-blue-50 via-white to-blue-100 py-16">
        <div className="max-w-7xl mx-auto px-6">
          <h3 className="text-2xl font-bold text-gray-800 flex items-center justify-center mb-12">
            <span className="w-12 h-12 border-2 border-blue-500 rounded-lg flex items-center justify-center mr-3">
              <Shield className="text-blue-600 w-7 h-7" />
            </span>
            Why Bike Insurance for Honda CB750 Hornet Is Crucial
          </h3>
          <div className="grid gap-6 md:grid-cols-4">
            <div className="bg-red-50 border border-red-200 rounded-xl p-6 shadow-md hover:shadow-lg transition">
              <div className="w-12 h-12 flex items-center justify-center rounded-full bg-white border border-red-500 mb-4">
                <FaGavel className="w-6 h-6 text-red-600" />
              </div>
              <h4 className="text-lg font-semibold text-red-700 mb-2">Mandatory by Law</h4>
              <p className="text-red-600 text-sm leading-relaxed">
                Third-party liability insurance is required under the Motor Vehicles Act.
              </p>
            </div>
            <div className="bg-green-50 border border-green-200 rounded-xl p-6 shadow-md hover:shadow-lg transition">
              <div className="w-12 h-12 flex items-center justify-center rounded-full bg-white border border-green-500 mb-4">
                <DollarSign className="w-6 h-6 text-green-600" />
              </div>
              <h4 className="text-lg font-semibold text-green-700 mb-2">High-Stakes Investment</h4>
              <p className="text-green-600 text-sm leading-relaxed">
                With premium pricing and performance parts, repair costs can be substantial.
              </p>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 shadow-md hover:shadow-lg transition">
              <div className="w-12 h-12 flex items-center justify-center rounded-full bg-white border border-blue-500 mb-4">
                <Shield className="w-6 h-6 text-blue-600" />
              </div>
              <h4 className="text-lg font-semibold text-blue-700 mb-2">Comprehensive Safeguard</h4>
              <p className="text-blue-600 text-sm leading-relaxed">
                Ensures protection from theft, accidents, fire, and natural calamities.
              </p>
            </div>
            <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-6 shadow-md hover:shadow-lg transition">
              <div className="w-12 h-12 flex items-center justify-center rounded-full bg-white border border-indigo-500 mb-4">
                <ShieldCheck className="w-6 h-6 text-indigo-600" />
              </div>
              <h4 className="text-lg font-semibold text-indigo-700 mb-2">Add-On Value</h4>
              <p className="text-indigo-600 text-sm leading-relaxed">
                Covers like zero depreciation, engine protection, and roadside assistance are especially beneficial given the premium nature of the bike.
              </p>
            </div>
          </div>
        </div>
      </div>
      <div className="px-6 mt-6 md:px-14">
        <div className="max-w-7xl mx-auto px-6 bg-white rounded-2xl shadow-xl p-8">
          <motion.h3
            initial={{ opacity: 0, x: -40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7 }}
            className="text-2xl font-bold text-gray-800 mb-10 flex items-center"
          >
            <div className="w-10 h-10 border-2 border-purple-500 rounded-lg flex items-center justify-center mr-3">
              <FileText className="text-purple-600 w-6 h-6" />
            </div>
            Types of Insurance Policies for Honda CB750 Hornet
          </motion.h3>
          <div className="space-y-6">
            {[
              {
                id: 1,
                title: "Third-Party Liability Insurance",
                border: "orange",
                points: [
                  "Compulsory by law.",
                  "Covers third-party injury and property damage.",
                  "Does not cover damage to the CB750 Hornet itself.",
                ],
              },
              {
                id: 2,
                title: "Comprehensive Bike Insurance",
                border: "teal",
                points: [
                  "Covers both third-party liabilities and damages to your own bike.",
                  "Protection against theft, accidents, fire, and natural disasters.",
                  "Expandable with a range of valuable add-ons.",
                ],
              },
              {
                id: 3,
                title: "Standalone Own-Damage (OD) Insurance",
                border: "indigo",
                points: [
                  "Requires valid third-party coverage.",
                  "Covers only damage-related losses to your CB750 Hornet due to mishaps, theft, or disasters.",
                ],
              },
            ].map((policy, i) => (
              <motion.div
                key={policy.id}
                custom={i}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={cardVariants}
                whileHover={{ scale: 1.03, transition: { type: "spring", stiffness: 200 } }}
                className={`border-l-4 border-${policy.border}-500 bg-${policy.border}-50 p-6 rounded-r-xl shadow-sm hover:shadow-md transition`}
              >
                <div className="flex items-start space-x-4">
                  <div
                    className={`flex-shrink-0 w-8 h-8 border border-${policy.border}-500 rounded-full bg-white flex items-center justify-center text-${policy.border}-600 font-bold text-sm`}
                  >
                    {policy.id}
                  </div>
                  <div className="flex-1">
                    <h4 className={`text-lg font-semibold text-${policy.border}-700 mb-3`}>{policy.title}</h4>
                    <ul className={`space-y-2 text-${policy.border}-600 text-sm leading-relaxed`}>
                      {policy.points.map((point, idx) => (
                        <li key={idx} className="flex items-start">
                          <span className={`w-1.5 h-1.5 bg-${policy.border}-500 rounded-full mt-2 mr-3`}></span>
                          {point}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
      <section className="bg-blue-50 py-6 px-4 sm:px-6 md:px-20 lg:px-20 rounded-3xl shadow-lg max-w-6xl mx-auto mt-10">
        <div className="flex flex-col md:flex-row justify-between items-center gap-10">
          <div className="max-w-xl space-y-6 text-center md:text-left">
            <h2 className="text-2xl sm:text-3xl font-bold text-blue-900 leading-tight">
              Calculate Your Bike Insurance <br />
              in <span className="text-blue-600">Seconds</span>
            </h2>
            <p className="text-gray-700 text-lg">
              Use our fast and free calculator to estimate your premium — no paperwork, no waiting.
            </p>
            <Link
              to="/financial-tools-calculators/bike-insurance-calculator"
              className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 text-lg rounded-full shadow-md transition"
            >
              Start Calculating <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
          <div className="space-y-4 text-left">
            {features.map((item, index) => (
              <div key={index} className="flex items-center gap-3">
                <span className="text-xl">{item.icon}</span>
                <span className="text-gray-800 font-medium">{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>
      <div className="px-6 mt-10 md:px-14">
        <div className="max-w-7xl mx-auto px-6 bg-white rounded-2xl shadow-xl p-8">
          <h3 className="text-2xl font-bold text-gray-800 mb-6">
            Estimated Annual Premium for CB750 Hornet Insurance
          </h3>
          <p className="text-gray-600 text-sm mb-8">
            Premium estimates vary based on city, model year, IDV, add-ons, and No-Claim Bonus.
          </p>
          <div className="overflow-hidden rounded-xl border border-gray-200 shadow-sm">
            <div className="grid grid-cols-2 bg-gray-100 text-gray-700 font-semibold text-sm">
              <div className="p-4">Policy Type</div>
              <div className="p-4">Estimated Annual Premium</div>
            </div>
            {tableData.map((row, i) => (
              <div
                key={i}
                className="grid grid-cols-2 border-t border-gray-200 text-sm"
              >
                <div className={`p-4 font-medium text-${row.color}-700 flex items-center`}>
                  <span className={`w-2 h-2 rounded-full bg-${row.color}-500 mr-2`}></span>
                  {row.type}
                </div>
                <div className={`p-4 text-${row.color}-600 font-semibold`}>{row.premium}</div>
              </div>
            ))}
          </div>
          <p className="text-gray-500 text-xs mt-4">
            (Premium estimates vary based on city, model year, IDV, add-ons, and No-Claim Bonus.)
          </p>
        </div>
      </div>
      <div className="w-full flex justify-center items-center my-6 px-4">
        <div className="w-full max-w-[984px]">
          <AdBanner displayOrder="3" />
        </div>
      </div>
      <div className="container mx-auto p-6 mt-10">
        <h2 className="text-2xl md:text-3xl font-semibold text-gray-800 text-center">
          Recommended Add-On Covers
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 mt-4">
          {cardsNew.map((card, index) => (
            <div
              key={index}
              className="border rounded-lg shadow-lg p-6 flex flex-col items-center text-center"
            >
              {card.icon}
              <h3 className="text-lg font-semibold mb-2">{card.title}</h3>
              <p className="text-gray-600">{card.description}</p>
            </div>
          ))}
        </div>
      </div>
      <div className="w-full px-6 md:px-14 py-6 bg-gradient-to-br from-sky-50 via-white to-sky-100 dark:from-gray-900 dark:to-gray-800">
        <div className="max-w-7xl mx-auto grid gap-10">
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-lg p-6 md:p-10 border border-emerald-200 dark:border-gray-700 hover:shadow-xl transition-shadow">
            <div className="flex items-center gap-3 mb-6">
              <BadgeCheck className="w-8 h-8 text-emerald-600" />
              <span className="text-2xl md:text-3xl font-bold text-gray-800 dark:text-white">
                Factors That Influence Premiums
              </span>
            </div>
            <div className="grid sm:grid-cols-2 gap-6 text-gray-700 dark:text-gray-300">
              <div className="p-5 bg-emerald-50 dark:bg-gray-800 rounded-xl">
                <span>Bike Details: Year, variant, and valuation (IDV).</span>
              </div>
              <div className="p-5 bg-emerald-50 dark:bg-gray-800 rounded-xl">
                <span>Location: Metro vs. Non-metro regions.</span>
              </div>
              <div className="p-5 bg-emerald-50 dark:bg-gray-800 rounded-xl">
                <span>Coverage Level: Chosen policy type and number of add-ons.</span>
              </div>
              <div className="p-5 bg-emerald-50 dark:bg-gray-800 rounded-xl">
                <span>Insurer Rewards: Available No-Claim Bonus.</span>
              </div>
            </div>
          </div>
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-lg p-6 md:p-10 border border-sky-200 dark:border-gray-700 hover:shadow-xl transition-shadow">
            <div className="flex items-center gap-3 mb-2">
              <FileText className="w-8 h-8 text-sky-600" />
              <span className="text-2xl md:text-3xl font-bold text-gray-800 dark:text-white">
                Required Documents
              </span>
            </div>
            <div className="grid sm:grid-cols-2 gap-6 text-gray-700 dark:text-gray-300">
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>Insurance policy copy.</span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>Registration Certificate (RC).</span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>KYC documents (PAN/Aadhaar).</span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>Passport-size photo.</span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>Valid driving license.</span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>FIR (if theft or serious accident occurs).</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="px-6 mt-10 md:px-14 mb-4">
        <div className="max-w-7xl mx-auto px-6 bg-white rounded-2xl shadow-xl p-8">
          <h3 className="text-2xl font-bold text-gray-800 mb-10">
            Claim Process for Honda CB750 Hornet
          </h3>
          <div className="relative">
            <span className="absolute left-4 top-0 bottom-0 w-0.5 bg-purple-200"></span>
            <ul className="space-y-8">
              {steps.map((step, idx) => (
                <li key={idx} className="relative flex items-start">
                  <span className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full bg-blue-500 text-white font-semibold z-10">
                    {idx + 1}
                  </span>
                  <div className="ml-6 bg-gray-50 border border-gray-200 rounded-xl shadow-sm p-4 flex-1">
                    <p className="text-gray-700 text-sm md:text-base leading-relaxed">
                      {step}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
      <div className="max-w-4xl mx-auto py-6 px-2">
        <h2 className="text-2xl md:text-3xl font-semibold text-center mb-8">
          Frequently Asked Questions
        </h2>
        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className="border rounded-lg overflow-hidden shadow-md"
            >
              <button
                onClick={() => toggleAccordion(index)}
                className="w-full p-4 text-left text-lg font-semibold focus:outline-none flex justify-between items-center"
              >
                {faq.question}
                <Icon id={index} open={activeIndex} />
              </button>
              {activeIndex === index && (
                <div className="p-4 bg-gray-100">
                  <p className="text-gray-700">{faq.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
      <section className="relative py-20 px-4 sm:px-8 bg-gradient-to-br from-white to-gray-100 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none z-0">
          <div className="absolute top-[-20%] left-[-10%] w-96 h-96 bg-indigo-300 opacity-30 blur-3xl rounded-full"></div>
          <div className="absolute bottom-[-10%] right-[-10%] w-96 h-96 bg-purple-300 opacity-30 blur-3xl rounded-full"></div>
        </div>
        <motion.div
          initial={{ opacity: 0, y: 60 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          viewport={{ once: true }}
          className="relative z-10 max-w-4xl mx-auto bg-white/90 backdrop-blur-sm border border-gray-200 shadow-xl rounded-2xl p-10"
        >
          <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
            <div className="text-indigo-600 text-5xl">
              <FaRoad />
            </div>
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
                Conclusion
              </h2>
              <p className="text-gray-700 text-lg leading-relaxed">
                The Honda CB750 Hornet is a standout offering in the mid-cap performance segment—packing power, style, and advanced tech. Given its premium cost, opting for a comprehensive insurance plan with essential add-ons ensures you're fully protected emotionally, legally, and financially. Platforms like TheFinMaster make it easy to compare, customize, and purchase insurance conveniently online.
              </p>
            </div>
          </div>
        </motion.div>
      </section>
    </>
  );
};

export default HondaCB750HornetInsurance;